import { useMemo, useState, type SyntheticEvent } from 'react';
import { useMutation } from '@apollo/client';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, LoaderGlobal, Modal } from '@bloomays-lib/ui.shared';
import { PageHeader } from '../molecules/PageHeader';
import {
  CREATE_PURCHASE_ORDER,
  DELETE_PURCHASE_ORDER,
  UPDATE_PURCHASE_ORDER,
  UPLOAD_PURCHASE_ORDER_FILE,
} from '@bloomays-lib/adapter.api-bloomer';
import { Role } from '@bloomays-lib/types.shared';
import { convertDateToStringFormat } from '@bloomays-lib/utils.shared';
import { useAuthStore } from '../../stores';
import { usePurchaseOrderDashboard } from '../../hooks/usePurchaseOrderDashboard';
import {
  PurchaseOrderFormModal,
  PurchaseOrderFormSubmitContext,
  PurchaseOrderFormValues,
} from '../organisms/PurchaseOrderFormModal';
import { ReceivedPurchaseOrderTab } from '../organisms/ReceivedPurchaseOrderTab';
import { MissingPurchaseOrderTab } from '../organisms/MissingPurchaseOrderTab';
import { PurchaseOrderKpiCards } from '../organisms/PurchaseOrderKpiCards';
import { PurchaseOrderCoverageTab } from '../organisms/PurchaseOrderCoverageTab';
import {
  DashboardPurchaseOrder,
  MissingMonthKey,
  MissingPurchaseOrderRow,
  parsePurchaseOrderDashboardSearchParams,
  type PurchaseOrderDashboardTab,
} from '../../helpers/purchaseOrderDashboard';
import { cancel, notify } from '../../helpers/toastify';
import { errorLogger } from '../../helpers/error';

export type { PurchaseOrderDashboardTab };

const toDateInput = (value: string | Date | null | undefined): string | undefined =>
  value ? convertDateToStringFormat(value as string, 'yyyy-MM-dd') : undefined;

const PurchaseOrderDashboard = () => {
  const { t } = useTranslation(['purchaseOrderDashboard']);
  const auth = useAuthStore((state) => state.auth);
  const isAdmin = auth?.user?.roles?.includes(Role.Admin) || false;
  const isSystem = auth?.user?.roles?.includes(Role.System) || false;

  const [searchParams] = useSearchParams();
  const deeplink = useMemo(() => parsePurchaseOrderDashboardSearchParams(searchParams), [searchParams]);

  const [activeTab, setActiveTab] = useState<PurchaseOrderDashboardTab>(() => deeplink.tab ?? 'coverage');
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editTarget, setEditTarget] = useState<DashboardPurchaseOrder | null>(null);
  const [createInitialValues, setCreateInitialValues] = useState<Partial<PurchaseOrderFormValues> | undefined>(
    undefined,
  );
  const [deleteTarget, setDeleteTarget] = useState<DashboardPurchaseOrder | null>(null);
  const [bulkDeleteTargets, setBulkDeleteTargets] = useState<DashboardPurchaseOrder[]>([]);
  const [pending, setPending] = useState(false);
  const [pendingRecordId, setPendingRecordId] = useState<string | null>(null);
  const [requestedMonthFilter, setRequestedMonthFilter] = useState<MissingMonthKey | undefined>(undefined);
  const [requestedCoverageFilter, setRequestedCoverageFilter] = useState<'gap' | 'ok' | undefined>(undefined);

  const handleTabChange = (_event: SyntheticEvent, value: PurchaseOrderDashboardTab) => {
    setActiveTab(value);
    if (value !== 'missing') {
      setRequestedMonthFilter(undefined);
    }
    if (value !== 'coverage') {
      setRequestedCoverageFilter(undefined);
    }
  };

  const dashboard = usePurchaseOrderDashboard();
  const [createPurchaseOrder] = useMutation(CREATE_PURCHASE_ORDER);
  const [updatePurchaseOrder] = useMutation(UPDATE_PURCHASE_ORDER);
  const [deletePurchaseOrder] = useMutation(DELETE_PURCHASE_ORDER);
  const [uploadPurchaseOrderFile] = useMutation(UPLOAD_PURCHASE_ORDER_FILE);

  const missionLabelByRecordId = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    dashboard.coverages.forEach((coverage) => {
      map[coverage.missionRecordId] = coverage.missionLabel ?? '';
    });
    return map;
  }, [dashboard.coverages]);

  const clientByMissionRecordId = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    dashboard.coverages.forEach((coverage) => {
      map[coverage.missionRecordId] = coverage.clientSocietyName ?? '';
    });
    return map;
  }, [dashboard.coverages]);

  const editInitialValues = useMemo<Partial<PurchaseOrderFormValues> | undefined>(() => {
    if (!editTarget) {
      return undefined;
    }
    const missionIds = editTarget.missionsRecordId ?? [];
    const clientSocietyName = missionIds
      .map(
        (missionRecordId) =>
          clientByMissionRecordId[missionRecordId] ||
          dashboard.missionEnrichmentByRecordId[missionRecordId]?.clientSocietyName,
      )
      .find((name) => !!name);
    return {
      orderNumber: editTarget.orderNumber,
      clientSocietyRecordId: editTarget.clientSocietyRecordId ?? undefined,
      clientSocietyName,
      missionsRecordId: missionIds,
      initialMissions: missionIds.map((missionRecordId) => ({
        id: missionRecordId,
        name:
          missionLabelByRecordId[missionRecordId] ||
          dashboard.missionEnrichmentByRecordId[missionRecordId]?.missionLabel ||
          missionRecordId,
        clientSocietyRecordId: editTarget.clientSocietyRecordId ?? undefined,
      })),
      startDate: toDateInput(editTarget.startDate),
      endDate: toDateInput(editTarget.endDate),
      amountHT: editTarget.amountHT ?? undefined,
      invoicedAmountHT: editTarget.invoicedAmountHT ?? undefined,
      invoiceComment: editTarget.invoiceComment ?? undefined,
      useForNonWorkingHours: editTarget.useForNonWorkingHours ?? undefined,
    };
  }, [editTarget, clientByMissionRecordId, missionLabelByRecordId, dashboard.missionEnrichmentByRecordId]);

  if (!isAdmin && !isSystem) {
    return <Navigate to="/forbidden/norole" replace />;
  }

  const openCreate = () => {
    setFormMode('create');
    setEditTarget(null);
    setCreateInitialValues(undefined);
    setFormOpen(true);
  };

  const openCreateForMissionRecord = (missionRecordId: string, missionLabel: string, clientSocietyName?: string) => {
    setFormMode('create');
    setEditTarget(null);
    setCreateInitialValues({
      missionsRecordId: [missionRecordId],
      clientSocietyName: clientSocietyName || undefined,
      initialMissions: [
        {
          id: missionRecordId,
          name: missionLabel || missionRecordId,
        },
      ],
    });
    setFormOpen(true);
  };

  const openCreateForMission = (row: MissingPurchaseOrderRow) => {
    openCreateForMissionRecord(row.missionRecordId, row.missionLabel, row.clientSocietyName);
  };

  const openEdit = (purchaseOrder: DashboardPurchaseOrder) => {
    setFormMode('edit');
    setEditTarget(purchaseOrder);
    setFormOpen(true);
  };

  const handleSubmitForm = async (values: PurchaseOrderFormValues, context?: PurchaseOrderFormSubmitContext) => {
    try {
      setPending(true);
      if (formMode === 'create') {
        let createdPurchaseOrder: DashboardPurchaseOrder | undefined;
        try {
          const createResult = await createPurchaseOrder({
            variables: {
              input: {
                orderNumber: values.orderNumber,
                clientSocietyRecordId: values.clientSocietyRecordId,
                missionsRecordId: values.missionsRecordId,
                startDate: values.startDate,
                endDate: values.endDate,
                amountHT: values.amountHT,
                useForNonWorkingHours: values.useForNonWorkingHours,
                invoiceComment: values.invoiceComment,
              },
            },
          });
          createdPurchaseOrder = createResult.data?.createPurchaseOrder;
        } catch (error) {
          cancel();
          errorLogger(error as Error, { extraInfos: 'TEC-2890 create purchase order' });
          notify('error', t('createError'));
          return;
        }

        const createdRecordId = createdPurchaseOrder?.recordId;
        if (context?.file && createdRecordId) {
          try {
            await uploadPurchaseOrderFile({
              variables: { input: { recordId: createdRecordId, file: context.file } },
            });
            notify('success', t('uploadSuccess'));
          } catch (error) {
            cancel();
            errorLogger(error as Error, { extraInfos: 'TEC-2890 upload purchase order file after create' });
            notify('error', t('createUploadError'));
            await dashboard.refetchAll();
            if (createdPurchaseOrder) {
              setFormMode('edit');
              setEditTarget(createdPurchaseOrder);
              setCreateInitialValues(undefined);
            }
            return;
          }
        } else {
          notify('success', t('createSuccess'));
        }
        setFormOpen(false);
        await dashboard.refetchAll();
        return;
      }

      if (editTarget?.recordId) {
        await updatePurchaseOrder({
          variables: {
            input: {
              recordId: editTarget.recordId,
              orderNumber: values.orderNumber,
              clientSocietyRecordId: values.clientSocietyRecordId,
              missionsRecordId: values.missionsRecordId,
              startDate: values.startDate,
              endDate: values.endDate,
              amountHT: values.amountHT,
              invoicedAmountHT: values.invoicedAmountHT,
              useForNonWorkingHours: values.useForNonWorkingHours,
              invoiceComment: values.invoiceComment,
            },
          },
        });
        notify('success', t('updateSuccess'));
        setFormOpen(false);
        await dashboard.refetchAll();
      }
    } catch (error) {
      cancel();
      errorLogger(error as Error, { extraInfos: 'TEC-2890 save purchase order' });
      notify('error', t('updateError'));
    } finally {
      setPending(false);
    }
  };

  const handleUploadFromModal = async (file: File) => {
    if (!editTarget?.recordId) {
      return;
    }
    try {
      setPendingRecordId(editTarget.recordId);
      const uploadResult = await uploadPurchaseOrderFile({
        variables: { input: { recordId: editTarget.recordId, file } },
      });
      const updatedPurchaseOrder = uploadResult.data?.uploadPurchaseOrderFile;
      if (updatedPurchaseOrder) {
        setEditTarget(updatedPurchaseOrder);
      }
      notify('success', t('uploadSuccess'));
      await dashboard.refetchAll();
    } catch (error) {
      cancel();
      errorLogger(error as Error, { extraInfos: 'TEC-2890 upload purchase order file from modal' });
      notify('error', t('uploadError'));
    } finally {
      setPendingRecordId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.recordId) {
      setDeleteTarget(null);
      return;
    }
    try {
      setPending(true);
      await deletePurchaseOrder({ variables: { id: deleteTarget.recordId } });
      notify('success', t('deleteSuccess'));
      setDeleteTarget(null);
      await dashboard.refetchAll();
    } catch (error) {
      cancel();
      errorLogger(error as Error, { extraInfos: 'TEC-2890 delete purchase order' });
      notify('error', t('deleteError'));
    } finally {
      setPending(false);
    }
  };

  const confirmBulkDelete = async () => {
    const targets = bulkDeleteTargets.filter((purchaseOrder) => !!purchaseOrder.recordId);
    if (targets.length === 0) {
      setBulkDeleteTargets([]);
      return;
    }
    try {
      setPending(true);
      await Promise.all(
        targets.map((purchaseOrder) => deletePurchaseOrder({ variables: { id: purchaseOrder.recordId } })),
      );
      notify('success', t('bulkDeleteSuccess', { count: targets.length }));
      setBulkDeleteTargets([]);
      await dashboard.refetchAll();
    } catch (error) {
      cancel();
      errorLogger(error as Error, { extraInfos: 'TEC-2890 bulk delete purchase orders' });
      notify('error', t('bulkDeleteError'));
    } finally {
      setPending(false);
    }
  };

  const handleUpload = async (purchaseOrder: DashboardPurchaseOrder, file: File) => {
    if (!purchaseOrder.recordId) {
      return;
    }
    try {
      setPendingRecordId(purchaseOrder.recordId);
      await uploadPurchaseOrderFile({ variables: { input: { recordId: purchaseOrder.recordId, file } } });
      notify('success', t('uploadSuccess'));
      await dashboard.refetchAll();
    } catch (error) {
      cancel();
      errorLogger(error as Error, { extraInfos: 'TEC-2890 upload purchase order file' });
      notify('error', t('uploadError'));
    } finally {
      setPendingRecordId(null);
    }
  };

  return (
    <Container>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        lastUpdatedAt={dashboard.lastUpdatedAt}
        refreshLabel={t('refresh')}
        onRefresh={() =>
          dashboard.refetchAll().catch((error) => errorLogger(error as Error, { extraInfos: 'TEC-2890 refresh' }))
        }
        actions={<Button ariaLabel="purchase-order-new" textButton={t('newPurchaseOrder')} onClick={openCreate} />}
      />

      {dashboard.error ? (
        <ErrorText variant="body1Light">{t('loadingError')}</ErrorText>
      ) : dashboard.loading ? (
        <LoaderGlobal waitingText={t('loading')} />
      ) : (
        <>
          {dashboard.kpis ? (
            <KpiArea data-testid="purchase-order-kpis">
              <PurchaseOrderKpiCards
                kpis={dashboard.kpis}
                purchaseOrders={dashboard.purchaseOrders}
                referenceDate={dashboard.referenceDate}
                onSelectCoverage={() => {
                  setRequestedCoverageFilter('gap');
                  setRequestedMonthFilter(undefined);
                  setActiveTab('coverage');
                }}
                onSelectMissingMonth={(month) => {
                  setRequestedMonthFilter(month);
                  setRequestedCoverageFilter(undefined);
                  setActiveTab('missing');
                }}
              />
            </KpiArea>
          ) : null}

          <Tabs value={activeTab} onChange={handleTabChange} aria-label="purchase-order-tabs">
            <Tab value="coverage" label={`${t('tabCoverage')} (${dashboard.coverages.length})`} />
            <Tab value="missing" label={`${t('tabMissing')} (${dashboard.missingRows.length})`} />
            <Tab value="existing" label={`${t('tabExisting')} (${dashboard.purchaseOrders.length})`} />
          </Tabs>

          <TabPanel role="tabpanel" data-testid={`purchase-order-tab-${activeTab}`}>
            {activeTab === 'coverage' ? (
              <PurchaseOrderCoverageTab
                coverages={dashboard.coverages}
                purchaseOrders={dashboard.purchaseOrders}
                missionEnrichmentByRecordId={dashboard.missionEnrichmentByRecordId}
                referenceDate={dashboard.referenceDate}
                requestedCoverageFilter={requestedCoverageFilter}
                onRequestedCoverageFilterChange={setRequestedCoverageFilter}
                onCreateBdc={(coverage) =>
                  openCreateForMissionRecord(
                    coverage.missionRecordId,
                    coverage.missionLabel ?? coverage.missionRecordId,
                    coverage.clientSocietyName ?? undefined,
                  )
                }
                onEditPurchaseOrder={openEdit}
              />
            ) : null}
            {activeTab === 'existing' ? (
              <ReceivedPurchaseOrderTab
                purchaseOrders={dashboard.purchaseOrders}
                missionLabelByRecordId={missionLabelByRecordId}
                clientByMissionRecordId={clientByMissionRecordId}
                missionEnrichmentByRecordId={dashboard.missionEnrichmentByRecordId}
                pendingRecordId={pendingRecordId}
                initialSearchValue={deeplink.orderNumber}
                onEdit={openEdit}
                onDelete={(purchaseOrder) => setDeleteTarget(purchaseOrder)}
                onBulkDelete={(purchaseOrders) => setBulkDeleteTargets(purchaseOrders)}
                onUpload={handleUpload}
              />
            ) : null}
            {activeTab === 'missing' ? (
              <MissingPurchaseOrderTab
                missingRows={dashboard.missingRows}
                missionEnrichmentByRecordId={dashboard.missionEnrichmentByRecordId}
                referenceDate={dashboard.referenceDate}
                onCreate={openCreateForMission}
                requestedMonthFilter={requestedMonthFilter}
                onRequestedMonthFilterChange={setRequestedMonthFilter}
              />
            ) : null}
          </TabPanel>
        </>
      )}

      <PurchaseOrderFormModal
        open={formOpen}
        mode={formMode}
        recordId={formMode === 'edit' ? editTarget?.recordId : undefined}
        fileUrl={formMode === 'edit' ? editTarget?.fileUrl : undefined}
        initialValues={formMode === 'edit' ? editInitialValues : createInitialValues}
        pending={pending}
        uploadPending={!!editTarget?.recordId && pendingRecordId === editTarget.recordId}
        onSubmit={handleSubmitForm}
        onUploadFile={formMode === 'edit' ? handleUploadFromModal : undefined}
        onClose={() => setFormOpen(false)}
      />

      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        additionnalCSS={{ width: '40%', padding: '32px', borderRadius: '20px' }}
      >
        <ConfirmContainer>
          <Typography variant="heading6">{t('deleteConfirmTitle')}</Typography>
          <Typography variant="body1Light">{t('deleteConfirmText')}</Typography>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button textButton={t('deleteCancel')} onClick={() => setDeleteTarget(null)} />
            <Button
              ariaLabel="purchase-order-delete-confirm"
              textButton={t('deleteConfirm')}
              onClick={confirmDelete}
              disable={pending}
            />
          </Stack>
        </ConfirmContainer>
      </Modal>

      <Modal
        open={bulkDeleteTargets.length > 0}
        onClose={() => setBulkDeleteTargets([])}
        additionnalCSS={{ width: '40%', padding: '32px', borderRadius: '20px' }}
      >
        <ConfirmContainer>
          <Typography variant="heading6">{t('bulkDeleteConfirmTitle', { count: bulkDeleteTargets.length })}</Typography>
          <Typography variant="body1Light">
            {t('bulkDeleteConfirmText', { count: bulkDeleteTargets.length })}
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button textButton={t('deleteCancel')} onClick={() => setBulkDeleteTargets([])} />
            <Button
              ariaLabel="purchase-order-bulk-delete-confirm"
              textButton={t('deleteConfirm')}
              onClick={confirmBulkDelete}
              disable={pending}
            />
          </Stack>
        </ConfirmContainer>
      </Modal>
    </Container>
  );
};

export default PurchaseOrderDashboard;

const Container = styled('div')(({ theme }) => ({
  margin: 'auto',
  width: '90%',
}));

const ErrorText = styled(Typography)(({ theme }) => ({
  color: theme.palette.error?.main ?? '#991b1b',
  marginTop: 24,
}));

const KpiArea = styled(Box)(() => ({
  marginTop: 16,
}));

const TabPanel = styled(Box)(() => ({
  marginTop: 16,
}));

const ConfirmContainer = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
}));
